import React, { useEffect, useState } from "react";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import ParentSidebar from "../../components/ParentSidebar";

interface Appointment {
  id: string;
  patient_id: string;
  patient_name: string;
  type: string;
  appointment_description: string;
  start_time: string;
  end_time: string;
}

interface TimeInterval {
  start: string;
  end: string;
}

interface TherapyType {
  key: string;
  value: string;
}

const weekdays = ["Luni", "Marți", "Miercuri", "Joi", "Vineri"];

const PatientAppointments: React.FC = () => {
  const { token } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [timeIntervals, setTimeIntervals] = useState<TimeInterval[]>([]);
  const [therapyTypes, setTherapyTypes] = useState<TherapyType[]>([]);
  const [weekStart, setWeekStart] = useState<string>(() => getCurrentMonday());
  const [patientsCount, setPatientsCount] = useState<number>(0);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsedParent") === "true";
  });

  function getCurrentMonday(): string {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    const year = monday.getFullYear();
    const month = String(monday.getMonth() + 1).padStart(2, "0");
    const date = String(monday.getDate()).padStart(2, "0");
    return `${year}-${month}-${date}`;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apptRes = await fetch(`${API_BASE_URL}/appointments/patients-weekly`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ week_start: weekStart }),
        });
        const apptData = await apptRes.json();
        setAppointments(apptData.data || []);

        // detectăm câți copii are părintele pe baza datelor primite:
        const uniquePatients = new Set((apptData.data || []).map((a: Appointment) => a.patient_id));
        setPatientsCount(uniquePatients.size);

       // Trimitem weekStart ca parametru pentru a primi lista corectă de intervale
        const intervalRes = await fetch(`${API_BASE_URL}/types/time-intervals?date=${weekStart}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const intervalData = await intervalRes.json();
        setTimeIntervals(intervalData.data || []);

        const therapyRes = await fetch(`${API_BASE_URL}/types/therapy-type`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const therapyData = await therapyRes.json();
        setTherapyTypes(therapyData.data || []);

      } catch (err) {
        console.error("Eroare la încărcare:", err);
      }
    };

    fetchData();
  }, [token, weekStart]);

  const getDayDate = (offset: number): string => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + offset);
    return date.toISOString().split("T")[0];
  };

  const getTherapyTypeValue = (key: string) => {
    const therapy = therapyTypes.find((t) => t.key === key);
    return therapy ? therapy.value : key;
  };

  const renderCell = (interval: TimeInterval, date: string) => {
    const matches = appointments.filter(a => a.start_time === `${date}T${interval.start}:00`);
    if (matches.length === 0) return null;

    return (
      <div className="parent-appointment-cell">
        {matches.map((a) => (
          <div key={a.id} style={{ marginBottom: "10px" }}>
          <div>{getTherapyTypeValue(a.appointment_description)}</div>
          <div style={{ fontSize: "0.75rem", color: "#777", marginTop: "2px" }}>
            (Copil: {a.patient_name})
          </div>
        </div>
        ))}
      </div>
    );
  };

  const formatDateRO = (isoDate: string): string => {
    const [year, month, day] = isoDate.split("T")[0].split("-");
    return `${day}.${month}.${year}`;
  };

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <ParentSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />
      <div className="page-content">
        <h1 className="page-title">Programări</h1>
        <div className="table-top">
          <div className="page-subtitle">Săptămâna: {formatDateRO(weekStart)} - {formatDateRO(getDayDate(4))}</div>
          <div className="appointment-date">
            <label>Data: </label>
            <input
              type="date"
              value={weekStart}
              onChange={(e) => {
                const selected = new Date(e.target.value);
                const diff = selected.getDate() - selected.getDay() + (selected.getDay() === 0 ? -6 : 1);
                const monday = new Date(selected.setDate(diff)).toISOString().split("T")[0];
                setWeekStart(monday);
              }}
            />
          </div>
        </div>

        <div className="table-rounded-container">
          <table className="parent-appointments-table">
            <thead>
              <tr>
                <th>Ora</th>
                {weekdays.map((day, idx) => (
                  <th key={idx}>{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeIntervals.map((interval) => (
                <tr key={interval.start}>
                  <td>{`${interval.start} - ${interval.end}`}</td>
                  {[0, 1, 2, 3, 4].map((dayOffset) => (
                    <td key={dayOffset}>{renderCell(interval, getDayDate(dayOffset))}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PatientAppointments;